import {
  ActionIcon,
  Box,
  Button,
  Fieldset,
  Group,
  LoadingOverlay,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

import { useConfigTelegramChatRouting } from '../hooks/use-config-telegram-chat-routing';
import { TelegramRelayChats } from './telegram-relay-chats';

const lookupFieldOptions = [
  { value: 'payment_id', label: 'ID заявки' },
  { value: 'remote_id', label: 'Remote ID' },
  { value: 'client_id', label: 'ID клиента' },
];

export const TelegramChatRouting = () => {
  const {
    form,
    isLoading,
    shopOptions,
    paymentSystemOptions,
    addClientChat,
    removeClientChat,
    addRoute,
    removeRoute,
    addRelayChat,
    removeRelayChat,
    handleSubmit,
  } = useConfigTelegramChatRouting();
  const clientChats = form.getValues().clientChats;

  return (
    <Box pos="relative">
      <LoadingOverlay visible={isLoading} overlayProps={{ blur: 2 }} />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          <Box>
            <Title order={5}>Маршрутизация чеков</Title>
            <Text size="sm" c="dimmed" mt={4}>
              Пересылает чеки из клиентских чатов в чат соответствующей платёжной системы.
              Для клиентов без заявок настройте чаты проверки ниже.
            </Text>
          </Box>

          <Switch
            label="Включить маршрутизацию"
            description="Бот начнёт принимать чеки после сохранения настроек"
            key={form.key('enabled')}
            {...form.getInputProps('enabled', { type: 'checkbox' })}
          />

          <TextInput
            label="Операторы"
            placeholder="@operator1 @operator2"
            description="Бот отметит их, если не сможет передать чек в чат платёжной системы"
            key={form.key('operators')}
            {...form.getInputProps('operators')}
          />

          {form.errors.clientChats && typeof form.errors.clientChats === 'string' && (
            <Text size="sm" c="red">
              {form.errors.clientChats}
            </Text>
          )}

          {clientChats.map((clientChat, clientChatIndex) => (
            <Fieldset
              key={form.key(`clientChats.${clientChatIndex}`)}
              legend={`Клиентский чат ${clientChatIndex + 1}`}
            >
              <Stack gap="md">
                <Group justify="flex-end">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Удалить клиентский чат"
                    onClick={() => removeClientChat(clientChatIndex)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Group>

                <TextInput
                  label="Название"
                  placeholder="Например, Переводы Millioner"
                  withAsterisk
                  key={form.key(`clientChats.${clientChatIndex}.name`)}
                  {...form.getInputProps(`clientChats.${clientChatIndex}.name`)}
                />

                <TextInput
                  label="ID клиентского чата"
                  placeholder="Например, -1001234567890"
                  withAsterisk
                  key={form.key(`clientChats.${clientChatIndex}.chatId`)}
                  {...form.getInputProps(`clientChats.${clientChatIndex}.chatId`)}
                />

                <MultiSelect
                  label="Где искать ID"
                  description="Если найдено несколько незавершённых заявок, бот позовёт операторов"
                  data={lookupFieldOptions}
                  withAsterisk
                  key={form.key(`clientChats.${clientChatIndex}.lookupFields`)}
                  {...form.getInputProps(`clientChats.${clientChatIndex}.lookupFields`)}
                />

                <MultiSelect
                  label="Магазины"
                  placeholder="Все магазины"
                  description="Если ничего не выбрано, поиск выполняется по всем магазинам"
                  data={shopOptions}
                  searchable
                  clearable
                  nothingFoundMessage="Магазины не найдены"
                  key={form.key(`clientChats.${clientChatIndex}.shopIds`)}
                  {...form.getInputProps(`clientChats.${clientChatIndex}.shopIds`)}
                />

                <Fieldset legend="Чаты платёжных систем">
                  <Stack gap="md">
                    {clientChat.routes.map((_, routeIndex) => (
                      <Group
                        key={form.key(`clientChats.${clientChatIndex}.routes.${routeIndex}`)}
                        align="flex-end"
                        wrap="wrap"
                      >
                        <Select
                          label="Платёжная система"
                          placeholder="Выберите платёжную систему"
                          data={paymentSystemOptions}
                          searchable
                          allowDeselect={false}
                          withAsterisk
                          style={{ flex: 1, minWidth: 220 }}
                          key={form.key(
                            `clientChats.${clientChatIndex}.routes.${routeIndex}.paymentSystem`
                          )}
                          {...form.getInputProps(
                            `clientChats.${clientChatIndex}.routes.${routeIndex}.paymentSystem`
                          )}
                        />

                        <TextInput
                          label="ID чата платёжной системы"
                          placeholder="Например, -1009876543210"
                          withAsterisk
                          style={{ flex: 1, minWidth: 220 }}
                          key={form.key(
                            `clientChats.${clientChatIndex}.routes.${routeIndex}.chatId`
                          )}
                          {...form.getInputProps(
                            `clientChats.${clientChatIndex}.routes.${routeIndex}.chatId`
                          )}
                        />

                        {clientChat.routes.length > 1 && (
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            mb={4}
                            aria-label="Удалить маршрут"
                            onClick={() => removeRoute(clientChatIndex, routeIndex)}
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        )}
                      </Group>
                    ))}

                    <Group justify="flex-start">
                      <Button
                        type="button"
                        variant="light"
                        leftSection={<IconPlus size={16} />}
                        onClick={() => addRoute(clientChatIndex)}
                      >
                        Добавить платёжную систему
                      </Button>
                    </Group>
                  </Stack>
                </Fieldset>
              </Stack>
            </Fieldset>
          ))}

          <Group justify="flex-start">
            <Button
              type="button"
              variant="outline"
              leftSection={<IconPlus size={16} />}
              onClick={addClientChat}
            >
              Добавить клиентский чат
            </Button>
          </Group>

          <TelegramRelayChats form={form} onAdd={addRelayChat} onRemove={removeRelayChat} />

          <Group justify="flex-start">
            <Button type="submit" loading={isLoading}>
              Сохранить
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};
